import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { API_BASE } from './api';
import { installAxiosTiming, maybeLogFetchTiming } from './apiTiming';

// Helper to get auth token from localStorage
function getAuthToken(): string | null {
  const authData = localStorage.getItem('cd-people-auth');
  if (authData) {
    try {
      const { state } = JSON.parse(authData);
      return state.token || null;
    } catch {
      return null;
    }
  }
  return null;
}

// Helper to handle 401 responses - trigger Zustand logout
function handle401Response(response: Response) {
  if (response.status === 401) {
    // Call logout from Zustand store directly - this updates the state
    useAuth.getState().logout();
  }
}

// Create axios instance with auth interceptors
const chatAxios = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

installAxiosTiming(chatAxios, 'chat');

chatAxios.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

chatAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Call logout from Zustand store directly
      useAuth.getState().logout();
    }
    return Promise.reject(error);
  }
);

export interface ExtractedRequirements {
  keywords: string[];
  role?: string;
  skills_required?: string[];
  seniority?: string;
  location?: string[];
  confidence: number;
  missing_critical?: string[];
}

export interface SearchResult {
  matches: any[];
  extracted_requirements: ExtractedRequirements;
  confidence: number;
  should_ask_clarification: boolean;
  clarifying_question?: string;
  total_matches: number;
}

export const chatApi = {
  // Initialize chat session
  async init() {
    const { data } = await chatAxios.post('/chat/init');
    return data;
  },

  // Clear session and return a fresh one (new context)
  async clearAndReinit(currentSessionId: string | null) {
    if (currentSessionId) {
      try {
        await chatAxios.delete(`/chat/session/${encodeURIComponent(currentSessionId)}`);
      } catch (e) {
        console.warn('Session clear failed:', e);
      }
    }
    return chatApi.init();
  },

  // Extract requirements from query (lightweight, fast)
  async extract(query: string) {
    const { data } = await chatAxios.post('/chat/extract', { query });
    return data;
  },

  // Progressive search (instant DB filtering)
  async search(query: string, sessionId?: string): Promise<SearchResult> {
    const { data } = await chatAxios.post('/chat/search', {
      query,
      sessionId
    });
    return data;
  },

  // Full AI chat (with streaming)
  async sendMessage(
    message: string,
    sessionId: string,
    mode?: 'search' | 'team' | 'position' | 'candidates',
    signal?: AbortSignal
  ) {
    const token = getAuthToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const fetchStarted = performance.now();
    const chatUrl = `${API_BASE}/chat`;
    let response: Response;
    try {
      response = await fetch(chatUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message, sessionId, mode }),
        signal,
      });
    } catch (e) {
      maybeLogFetchTiming(
        'chat',
        'POST',
        chatUrl,
        fetchStarted,
        0,
        e instanceof Error ? e.message : 'fetch_error'
      );
      throw e;
    }

    // Time-to-headers (streaming body continues after this).
    maybeLogFetchTiming('chat', 'POST', chatUrl, fetchStarted, response.status);

    // Handle 401 for fetch requests
    if (response.status === 401) {
      handle401Response(response);
      throw new Error('Authentication required');
    }

    return response;
  }
};

